import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Pencil, Plus, Target } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { SummaryPitch, VideoPitch } from '@/types/pitch';
import { MAX_PITCHES, formatPitchTime, pitchLabel } from '@/utils/pitches';

interface PitchKpiCardProps {
  /** `video.pitches` do consolidado — define a lista e a ordem. */
  pitches: VideoPitch[];
  /** `summary.pitches` do consolidado — retenção de cada pitch, casada por id. */
  metrics: SummaryPitch[];
  /** Canetinha: edita o pitch `id`; null = adicionar um pitch novo. */
  onEdit: (pitchId: string | null) => void;
  /** Link "Detalhes" do pitch `id`. */
  onDetails: (pitchId: string) => void;
  className?: string;
}

/**
 * KPI "Retenção Pitch" das abas Visualizações e Retenção: uma linha por pitch
 * (nome · mm:ss · %), com canetinha e "Detalhes", e "+ Adicionar pitch" até o
 * limite. Sem pitch, mostra "Definir pitch".
 */
export function PitchKpiCard({ pitches, metrics, onEdit, onDetails, className }: PitchKpiCardProps) {
  useLanguage();
  const retentionById = new Map(metrics.map((m) => [m.id, m.retention_pct]));

  return (
    <Card
      className={cn(
        'md:min-h-[100px] rounded-[16px] border border-[#E4EBFF]',
        'shadow-[0_4px_10px_rgba(0,0,0,0.06)] md:shadow-[0_2px_10px_rgba(0,0,0,0.03)]',
        'bg-white md:bg-gradient-to-b md:from-white md:to-[#F9FBFF]',
        'flex min-w-0 flex-col justify-center gap-2 overflow-hidden p-4 md:px-[22px] md:py-4',
        className,
      )}
    >
      <div
        className="flex items-center gap-2"
        title={translateInterfaceText("Percentual de espectadores que ainda assistiam quando cada pitch começou.")}
      >
        <Target className="w-[18px] h-[18px] md:w-[24px] md:h-[24px] text-[#2F67FF]" strokeWidth={1.8} aria-hidden />
        <span className="text-[12px] md:text-[11px] font-medium text-[#7D8DA6] uppercase tracking-wide whitespace-nowrap">
          {translateInterfaceText("Retenção Pitch")}</span>
      </div>

      {pitches.length === 0 ? (
        <button
          type="button"
          onClick={() => onEdit(null)}
          className="self-start text-[16px] font-medium leading-none text-[#374151] hover:text-primary transition-colors"
        >
          {translateInterfaceText("Definir pitch")}</button>
      ) : (
        <ul className="space-y-1.5">
          {pitches.map((pitch, index) => {
            const label = pitchLabel(pitch, index);
            const retention = retentionById.get(pitch.id);
            return (
              // Duas linhas: o nome tem a largura toda do card (que ocupa só 2 de 7
              // colunas no desktop); tempo, retenção e ações vão na segunda.
              <li key={pitch.id} className="min-w-0 text-[13px] leading-5 text-[#374151]">
                <div className="truncate font-medium" title={label}>
                  {label}
                </div>
                <div className="flex items-center gap-2">
                  <span className="shrink-0 text-[#7D8DA6]">{formatPitchTime(pitch.seconds)} ·</span>
                  <span className="shrink-0 font-semibold text-[#1B1F3B]">
                    {retention != null ? `${retention.toFixed(1)}%` : '—'}
                  </span>
                  <button
                    type="button"
                    onClick={() => onEdit(pitch.id)}
                    aria-label={translateInterfaceText("Editar {value0}", { value0: (label) })}
                    className="ml-auto shrink-0 rounded p-1 transition-colors"
                  >
                    <Pencil className="h-3 w-3 md:h-3.5 md:w-3.5 text-[hsl(220,9%,64%)] hover:text-[hsl(217,91%,60%)] transition-colors" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onDetails(pitch.id)}
                    aria-label={translateInterfaceText("Detalhes de {value0}", { value0: (label) })}
                    className="shrink-0 whitespace-nowrap text-[10px] md:text-xs text-[hsl(220,9%,64%)] underline transition-colors hover:text-[hsl(217,91%,60%)]"
                  >
                    {translateInterfaceText("Detalhes")}</button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {pitches.length > 0 && pitches.length < MAX_PITCHES && (
        <button
          type="button"
          onClick={() => onEdit(null)}
          className="self-start inline-flex items-center gap-1 text-[12px] font-medium text-primary hover:underline underline-offset-2"
        >
          <Plus className="h-3 w-3" />
          {translateInterfaceText("Adicionar pitch")}</button>
      )}
    </Card>
  );
}
